/**
 * Semantic check and lowering (W1-10c core-expr): resolves names against the context's variable set, checks
 * function arities and units, and produces the evaluation IR (ir.ts) with explicit unit conversions.
 *
 * Units (the number you type is always in the field's own unit unless a unit says otherwise):
 *   px    pixels: a label, the number is unchanged.
 *   %     percent points: `= 50%` on Opacity is 50, `+= 10%` adds 10 points. A percentage multiplied by a plain
 *         factor (a number, i, n, u, pi, ease(...), ...) stays a percentage: `50% * 2` = 100%, `u * 100%` goes
 *         from 0% to 100%, `50%^2` = 25%. When it scales a value of the item (v, v0, v1, x, speed, size), a time,
 *         an angle or a distance it is a fraction: `v * 120%`, `t * 50%`, and so are `*= 50%` (halves),
 *         `v / 50%`, `lerp(a, b, 50%)` and the arguments of functions of plain numbers (`sqrt(25%)` = 0.5).
 *   deg   degrees: unchanged as a value; sin/cos/tan convert degrees to radians (`cos(60deg)` = 0.5).
 *   s ms  time, converted into the field's time unit, which the caller passes (`ExprParseOptions.unit`): on a
 *         millisecond field `= 1.5s` is 1500 and `+= 10ms` adds 10; on a seconds field `+= 10ms` adds 0.01. The
 *         variable `s` and `sec(...)` are seconds and convert the same way. A program whose result is a time
 *         written with a time unit is refused when the field's unit is not known (`Expr.parse` without a unit)
 *         or is not a time, because the conversion would be a guess (a silent factor of 1000). Time units that
 *         cancel (`2s / 500ms` = 4) or are converted to beats (`beat(1500ms)`) are always fine.
 *         Used as a plain number (either side of `^`, the arguments of functions of plain numbers such as sin,
 *         sqrt, ease or bezier, lerp's u, round's digits), a time counts in seconds on every field
 *         (`sin(s * pi)`). A time written with s or ms is refused there (`(500ms)^1`, `ease(1, 500ms)`,
 *         `sin(s + 500ms)`), whatever the field: wrapping a time must not slip past the rule above. Dividing by a
 *         time gives the number (`ease(1, 500ms / 1s)`, `sin((s + 500ms) / 1s)`).
 *   b     beats.
 *   v     RPE speed units over the item's segment (keys, segments, tables): `3v` is the distance travelled in
 *         the segment at speed 3, 120 * 3 * duration (s). Assigned with `=`, a pure distance (`3v`, `3v + 10`,
 *         `lerp(1v, 3v, u)`) is measured from the segment's start value: end = start + 120 * duration * v (RPE
 *         1.5 shorthand). An expression that uses a value of the item (v, v0, v1, ...) already states the whole
 *         value, so the distance is only added to it: `= v0 + 3v` is `= 3v`, `= v + 1v` is `+= 1v`. With
 *         `+=`/`-=` the distance is added to the current value.
 * Plain numbers combine with any unit (they take its unit). Beats and seconds never mix (convert with beat() and
 * sec()); other mixed units are errors with the range of the offending operation.
 *
 * Assignments: the motion path context (x(t), y(t), r(t)) has no current value, so only a value (optionally
 * after `=`) is accepted there; `+= 5`, `*2` and `v = 5` are refused.
 */

import { EASING_CATALOG, Ease } from '../ease';
import { EXPR_MSG, fail, type Span } from './errors';
import { findFunction, FUNCTION_NAMES, signatureOf, type FnSpec } from './functions';
import type { Ir, IrBinOp, Kind } from './ir';
import type { RawNode, RawProgram } from './parser';
import type { AssignOp, ExprVarSet } from './types';
import { ALL_VAR_NAMES, canonicalName, CONSTANTS, findConst, findVar, SPEED_UNIT_SETS, unitOf, VAR_SETS } from './vars';

/** Options beyond the contract's `parse(src, vars)`. */
export interface ExprParseOptions {
  /**
   * The field's display unit ("px", "%", "deg", "s", "ms", "beats", "x", ...). Time literals are converted
   * into a time unit (s or ms); a beat field refuses seconds and vice versa; a field of another unit refuses a
   * time written with s or ms; any other unit is accepted as a suffix meaning "in this field's unit" (`1.5x` on a
   * speed field). Without a unit, a result written with s or ms is refused (its conversion is unknown).
   */
  readonly unit?: string | undefined;
}

export interface Checked {
  readonly rhs: Ir;
  /** Scope fields read (keys of ExprScope, including 'durSec'), in canonical order. */
  readonly vars: readonly string[];
  readonly usesRand: boolean;
  /** True when the value does not depend on the item, the random generator or the tempo map. */
  readonly pure: boolean;
}

/** A time literal (`500ms`, `1.5s`): its value converts into the field's time unit. */
interface TimeLiteral extends Span {
  readonly unit: 's' | 'ms';
}

interface Typed {
  readonly ir: Ir;
  readonly kind: Kind;
  /**
   * The value is computed from a value of the item (VarSpec.itemValue: v, v0, v1, x, speed, size), not only
   * from factors (numbers, i, n, u, ...). Under `=`, a speed distance combined with such a value is not measured
   * from the segment start, and a percentage multiplying it is a fraction. A ratio of two item values
   * (`v / v1`) is a plain factor again.
   */
  readonly item: boolean;
  /** The first time literal that is part of this time value (null unless `kind` is 'time'). */
  readonly timeLit: TimeLiteral | null;
}

function typed(ir: Ir, kind: Kind, item = false, timeLit: TimeLiteral | null = null): Typed {
  return { ir, kind, item, timeLit: kind === 'time' ? timeLit : null };
}

type TimeUnit = 's' | 'ms';

const VAR_ORDER: readonly string[] = ['v', 'i', 'n', 'u', 't', 's', 'x', 'speed', 'size', 'v0', 'v1', 'len', 'durSec'];

const DEG_TO_RAD = Math.PI / 180;

interface FieldUnit {
  /** False when the caller did not pass the field's unit. */
  readonly known: boolean;
  readonly time: TimeUnit | 'beat' | null;
  /** Lower-cased unit accepted as an identity suffix (not one of the language's own units). */
  readonly suffix: string | null;
}

function fieldUnitOf(unit: string | undefined): FieldUnit {
  if (unit === undefined) return { known: false, time: null, suffix: null };
  const u = unit.trim().toLowerCase();
  if (u === '') return { known: false, time: null, suffix: null };
  if (['s', 'sec', 'secs', 'second', 'seconds'].includes(u)) return { known: true, time: 's', suffix: null };
  if (u === 'ms') return { known: true, time: 'ms', suffix: null };
  if (['b', 'beat', 'beats'].includes(u)) return { known: true, time: 'beat', suffix: unitOf(u) === null ? u : null };
  return { known: true, time: null, suffix: unitOf(u) === null && /^[a-z]+$/.test(u) ? u : null };
}

// ---------------------------------------------------------------------------------------------------------------
// Easing names
// ---------------------------------------------------------------------------------------------------------------

function compact(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const EASING_BY_NAME: ReadonlyMap<string, number> = (() => {
  const m = new Map<string, number>();
  for (const e of EASING_CATALOG) {
    for (const n of [e.rpeName, ...e.aliases]) {
      const k = compact(n);
      if (k !== '' && !m.has(k)) m.set(k, e.easingType);
    }
  }
  return m;
})();

/** RPE easing number for a curve name ("outQuad", "out quad", "easeOutQuad", "oq", "4"), or null. */
export function easingIdByName(name: string): number | null {
  return EASING_BY_NAME.get(compact(name)) ?? null;
}

/** RPE curve names in identifier form ("linear", "outSine", ...). */
const EASING_NAMES: readonly string[] = EASING_CATALOG.map((e) => camel(e.rpeName));

/** "OutQuad" -> "outQuad" (the identifier form shown in suggestions). */
function camel(rpeName: string): string {
  return rpeName.charAt(0).toLowerCase() + rpeName.slice(1);
}

// ---------------------------------------------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------------------------------------------

/** Optimal string alignment distance (Levenshtein with adjacent transpositions). */
function editDistance(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[] = new Array<number>(rows * cols).fill(0);
  const at = (i: number, j: number): number => d[i * cols + j] ?? 0;
  for (let i = 0; i < rows; i++) d[i * cols] = i;
  for (let j = 0; j < cols; j++) d[j] = j;
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(at(i - 1, j) + 1, at(i, j - 1) + 1, at(i - 1, j - 1) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, at(i - 2, j - 2) + 1);
      d[i * cols + j] = v;
    }
  }
  return at(a.length, b.length);
}

/** The closest candidate within a small edit distance, or null. */
export function suggest(name: string, candidates: readonly string[]): string | null {
  const n = name.toLowerCase();
  const limit = n.length <= 3 ? 1 : 2;
  let best: string | null = null;
  let bestD = Infinity;
  for (const c of candidates) {
    const dist = editDistance(n, c.toLowerCase());
    if (dist <= limit && dist < bestD && dist < Math.max(c.length, n.length)) {
      best = c;
      bestD = dist;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------------------------------------------
// Checker
// ---------------------------------------------------------------------------------------------------------------

function sp(n: Span): Span {
  return { from: n.from, to: n.to };
}

function numIr(value: number, span: Span): Ir {
  return { op: 'num', value, from: span.from, to: span.to };
}

function binIr(fn: IrBinOp, a: Ir, b: Ir, span: Span): Ir {
  return { op: 'bin', fn, a, b, from: span.from, to: span.to };
}

function scaleIr(arg: Ir, factor: number): Ir {
  if (arg.op === 'num') return { ...arg, value: arg.value * factor };
  return { op: 'scale', factor, arg, from: arg.from, to: arg.to };
}

/** x * 1000 by moving the decimal point, so typed decimals stay exact (1.1s = 1100 ms, not 1100.0000000000002). */
function times1000(x: number): number {
  const [mantissa = '0', exponent = '0'] = String(x).split('e');
  return Number(`${mantissa}e${String(Number(exponent) + 3)}`);
}

/**
 * Division variant of scaleIr: a real division (correctly rounded), not a product by 1 / divisor, so typed
 * decimals stay exact (500ms / 1000 = 0.5, 7% = 0.07).
 */
function divIr(arg: Ir, divisor: number): Ir {
  if (arg.op === 'num') return { ...arg, value: arg.value / divisor };
  return binIr('/', arg, numIr(divisor, arg), arg);
}

/** True when `text` is one operand as written (`500ms`, `s`, `(s + 1s)`), so it needs no parentheses. */
function isOperandText(text: string): boolean {
  if (/^[A-Za-z0-9_.]+$/.test(text)) return true;
  if (!text.startsWith('(') || !text.endsWith(')')) return false;
  let depth = 0;
  for (let k = 0; k < text.length; k++) {
    const c = text.charAt(k);
    if (c === '(') depth++;
    else if (c === ')') depth--;
    // The first "(" closes before the end: "(a) + (b)" is not one operand.
    if (depth === 0 && k < text.length - 1) return false;
  }
  return depth === 0;
}

class Checker {
  private readonly reads = new Set<string>();
  private usesRand = false;
  private impure = false;
  private readonly timeUnit: TimeUnit;
  private readonly src: string;
  private readonly varSet: ExprVarSet;
  private readonly field: FieldUnit;

  constructor(src: string, varSet: ExprVarSet, field: FieldUnit) {
    this.src = src;
    this.varSet = varSet;
    this.field = field;
    const ft = field.time;
    // Without a time field, time values are computed in seconds: the ones written with a unit are refused at
    // the end (see program), so only cancelling units, beat(...) arguments, `s` and sec(...) remain.
    this.timeUnit = ft === 's' || ft === 'ms' ? ft : 's';
  }

  private label(kind: Kind): string {
    switch (kind) {
      case 'num':
        return '1';
      case 'pct':
        return '%';
      case 'deg':
        return 'deg';
      case 'time':
        return this.timeUnit;
      case 'beat':
        return 'b';
      case 'vel':
        return 'v';
    }
  }

  private read(key: string): void {
    this.reads.add(key);
    this.impure = true;
  }

  program(raw: RawProgram): Checked {
    if (this.varSet === 'motionPath' && (raw.assign !== '=' || raw.target)) {
      // A motion path expression is the value itself: there is no current value to add to or name.
      const at = raw.assignSpan ?? raw.body;
      const op = raw.assignSpan === null ? raw.assign : this.src.slice(at.from, at.to).trim();
      fail(EXPR_MSG.assignHere, at, { op });
    }
    const body = this.check(raw.body);
    let rhs = body.ir;
    const ft = this.field.time;
    if (ft === 'beat' && body.kind === 'time') fail(EXPR_MSG.fieldWantsBeats, raw.body);
    if ((ft === 's' || ft === 'ms') && body.kind === 'beat') fail(EXPR_MSG.fieldWantsSeconds, raw.body);
    if (ft === null && body.timeLit !== null) {
      // `= 500ms` on a field whose unit is unknown or not a time: refused rather than guessed.
      const lit = body.timeLit;
      fail(this.field.known ? EXPR_MSG.fieldNotTime : EXPR_MSG.timeUnitUnknown, lit, { unit: lit.unit });
    }
    const assign: AssignOp = raw.assign;
    // The assignment reads the item's value, but the right-hand side itself may still be a constant.
    if (assign !== '=') this.reads.add('v');
    switch (assign) {
      case '=':
        // A pure distance is measured from the segment start; with a value of the item it is the whole value.
        if (body.kind === 'vel' && !body.item) {
          rhs = binIr('+', { op: 'segStart', from: raw.body.from, to: raw.body.to }, rhs, raw.body);
          this.read('v0');
        }
        break;
      case '+=':
      case '-=':
        break;
      case '*=':
      case '/=':
        if (body.kind === 'vel') fail(EXPR_MSG.scaleByUnit, raw.body, { op: assign });
        if (body.kind === 'pct') rhs = divIr(rhs, 100);
        break;
    }
    const vars = VAR_ORDER.filter((k) => this.reads.has(k));
    return { rhs, vars, usesRand: this.usesRand, pure: !this.impure };
  }

  check(node: RawNode): Typed {
    switch (node.t) {
      case 'num':
        return this.num(node);
      case 'id':
        return this.ident(node);
      case 'str':
        return fail(EXPR_MSG.stringHere, node);
      case 'neg': {
        const a = this.check(node.arg);
        const ir: Ir =
          a.ir.op === 'num' ? numIr(-a.ir.value, node) : { op: 'neg', arg: a.ir, from: node.from, to: node.to };
        return typed(ir, a.kind, a.item, a.timeLit);
      }
      case 'bin':
        return this.bin(node);
      case 'call':
        return this.call(node);
    }
  }

  private num(node: Extract<RawNode, { t: 'num' }>): Typed {
    const span = sp(node);
    if (node.unit === null) return typed(numIr(node.value, span), 'num');
    const unitSpan = { from: node.unitFrom, to: node.to };
    const unit = unitOf(node.unit);
    if (unit === null) {
      if (this.field.suffix !== null && node.unit.toLowerCase() === this.field.suffix) {
        return typed(numIr(node.value, span), 'num');
      }
      const canon = canonicalName(node.unit);
      if (ALL_VAR_NAMES.has(canon) || findConst(canon) !== undefined || findFunction(canon) !== undefined) {
        fail(EXPR_MSG.implicitMul, span, { num: this.src.slice(node.from, node.numTo), name: canon });
      }
      fail(EXPR_MSG.unknownUnit, unitSpan, { unit: node.unit });
    }
    switch (unit) {
      case 'px':
        return typed(numIr(node.value, span), 'num');
      case '%':
        return typed(numIr(node.value, span), 'pct');
      case 'deg':
        return typed(numIr(node.value, span), 'deg');
      case 's': {
        const value = this.timeUnit === 'ms' ? times1000(node.value) : node.value;
        return typed(numIr(value, span), 'time', false, { ...span, unit });
      }
      case 'ms': {
        const value = this.timeUnit === 's' ? node.value / 1000 : node.value;
        return typed(numIr(value, span), 'time', false, { ...span, unit });
      }
      case 'b':
        return typed(numIr(node.value, span), 'beat');
      case 'v':
        if (!SPEED_UNIT_SETS.has(this.varSet)) fail(EXPR_MSG.speedUnitHere, unitSpan);
        this.read('durSec');
        return typed({ op: 'vel', value: node.value, from: node.from, to: node.to }, 'vel');
    }
  }

  private available(): string[] {
    return [...VAR_SETS[this.varSet].map((v) => v.name), ...CONSTANTS.map((c) => c.name)];
  }

  private ident(node: Extract<RawNode, { t: 'id' }>): Typed {
    const name = canonicalName(node.name);
    const c = findConst(name);
    if (c !== undefined) return typed(numIr(c.value, node), 'num');
    const v = findVar(this.varSet, name);
    if (v !== undefined) {
      this.read(v.key);
      const ir: Ir = { op: 'var', key: v.key, name, from: node.from, to: node.to };
      // The variable s is in seconds; a millisecond program converts it like a seconds literal.
      return typed(v.kind === 'time' && this.timeUnit === 'ms' ? scaleIr(ir, 1000) : ir, v.kind, v.itemValue);
    }
    const f = findFunction(name);
    if (f !== undefined) fail(EXPR_MSG.needsCall, node, { name: f.name, signature: signatureOf(f) });
    if (ALL_VAR_NAMES.has(name)) fail(EXPR_MSG.varNotHere, node, { name, list: this.available().join(', ') });
    const s = suggest(name, [...this.available(), ...FUNCTION_NAMES]);
    if (s !== null) fail(EXPR_MSG.unknownNameSuggest, node, { name: node.name, suggestion: s });
    return fail(EXPR_MSG.unknownName, node, { name: node.name });
  }

  private unify(a: Kind, b: Kind, span: Span): Kind {
    if (a === b || b === 'num') return a;
    if (a === 'num') return b;
    if ((a === 'time' && b === 'beat') || (a === 'beat' && b === 'time')) fail(EXPR_MSG.mixedTime, span);
    return fail(EXPR_MSG.mixedUnits, span, { a: this.label(a), b: this.label(b) });
  }

  /** A time value of the program (in its time unit) expressed in seconds. */
  private toSeconds(ir: Ir): Ir {
    if (this.timeUnit !== 'ms') return ir;
    // `s` and sec(...) on a millisecond field are seconds scaled by 1000: take the seconds back unchanged.
    if (ir.op === 'scale' && ir.factor === 1000) return ir.arg;
    if (ir.op === 'neg') return { ...ir, arg: this.toSeconds(ir.arg) };
    return divIr(ir, 1000);
  }

  /**
   * A value used as a plain number: percent -> fraction, degrees -> radians (trig), a time in seconds (one
   * written with s or ms is refused), speed unit refused.
   */
  private numeric(t: Typed, trig: boolean): Ir {
    switch (t.kind) {
      case 'pct':
        return divIr(t.ir, 100);
      case 'deg':
        return trig ? scaleIr(t.ir, DEG_TO_RAD) : t.ir;
      case 'vel':
        return fail(EXPR_MSG.speedUnitOp, t.ir);
      case 'time': {
        if (t.timeLit !== null) {
          // `(500ms)^1`, `ease(1, 500ms)`: the number of a written time depends on its unit; dividing by a time
          // states it (`500ms / 1s`).
          const text = this.src.slice(t.ir.from, t.ir.to).trim();
          const example = text.length > 24 ? '(...) / 1s' : isOperandText(text) ? `${text} / 1s` : `(${text}) / 1s`;
          fail(EXPR_MSG.timeAsNumber, t.ir, { example });
        }
        return this.toSeconds(t.ir);
      }
      case 'num':
      case 'beat':
        return t.ir;
    }
  }

  private bin(node: Extract<RawNode, { t: 'bin' }>): Typed {
    const a = this.check(node.a);
    const b = this.check(node.b);
    const span = sp(node);
    const item = a.item || b.item;
    const lit = a.timeLit ?? b.timeLit;
    switch (node.op) {
      case '+':
      case '-':
        return typed(binIr(node.op, a.ir, b.ir, span), this.unify(a.kind, b.kind, span), item, lit);
      case '*':
        return this.product(a, b, span);
      case '/': {
        // A ratio of two values of the item (v / v1) is a plain factor.
        const ratioItem = a.item && b.item ? false : item;
        if (a.kind === b.kind && a.kind !== 'num') return typed(binIr('/', a.ir, b.ir, span), 'num', ratioItem);
        if (b.kind === 'pct') return typed(binIr('/', a.ir, divIr(b.ir, 100), span), a.kind, ratioItem, a.timeLit);
        if (b.kind === 'num') return typed(binIr('/', a.ir, b.ir, span), a.kind, ratioItem, a.timeLit);
        return fail(EXPR_MSG.unitQuotient, span, { b: this.label(b.kind) });
      }
      case '%':
        if (a.kind === 'vel' || b.kind === 'vel') fail(EXPR_MSG.speedUnitOp, span);
        return typed(binIr('%', a.ir, b.ir, span), this.unify(a.kind, b.kind, span), item, lit);
      case '^':
        return this.power(a, b, span);
    }
  }

  private product(a: Typed, b: Typed, span: Span): Typed {
    const item = a.item || b.item;
    const lit = a.timeLit ?? b.timeLit;
    const mul = (x: Ir, y: Ir): Ir => binIr('*', x, y, span);
    // 50% * 50% = 25%: the left one is a fraction of the right one.
    if (a.kind === 'pct' && b.kind === 'pct') return typed(mul(divIr(a.ir, 100), b.ir), 'pct', item);
    // A percentage times a plain factor stays a percentage: 50% * 2 = 100%, u * 100%, i * 10%.
    if (a.kind === 'pct' && b.kind === 'num' && !b.item) return typed(mul(a.ir, b.ir), 'pct', item);
    if (b.kind === 'pct' && a.kind === 'num' && !a.item) return typed(mul(a.ir, b.ir), 'pct', item);
    // Otherwise it is a fraction of what it scales: v * 120%, t * 50%, 2v * 50%.
    if (a.kind === 'pct') return typed(mul(divIr(a.ir, 100), b.ir), b.kind, item, lit);
    if (b.kind === 'pct') return typed(mul(a.ir, divIr(b.ir, 100)), a.kind, item, lit);
    if (a.kind === 'num') return typed(mul(a.ir, b.ir), b.kind, item, lit);
    if (b.kind === 'num') return typed(mul(a.ir, b.ir), a.kind, item, lit);
    return fail(EXPR_MSG.unitProduct, span, { a: this.label(a.kind), b: this.label(b.kind) });
  }

  private power(a: Typed, b: Typed, span: Span): Typed {
    const item = a.item || b.item;
    const exp = this.numeric(b, false);
    if (a.kind !== 'pct') return typed(binIr('^', this.numeric(a, false), exp, span), 'num', item);
    // A percentage to a power is computed on the fraction and stays a percentage: 50%^2 = 25%. Small whole
    // powers use p^k / 100^(k-1), which keeps typed decimals exact (10%^2 = 1%, not 1.0000000000000002%).
    if (exp.op === 'num' && Number.isInteger(exp.value) && exp.value >= 1 && exp.value <= 8) {
      const scaled = binIr('^', a.ir, exp, span);
      return typed(
        exp.value === 1 ? scaled : binIr('/', scaled, numIr(100 ** (exp.value - 1), span), span),
        'pct',
        item,
      );
    }
    return typed(scaleIr(binIr('^', divIr(a.ir, 100), exp, span), 100), 'pct', item);
  }

  private arity(f: FnSpec, node: Extract<RawNode, { t: 'call' }>): void {
    const n = node.args.length;
    if (n >= f.min && n <= f.max) return;
    const signature = signatureOf(f);
    if (f.min === f.max) fail(EXPR_MSG.argCount, node, { signature, count: f.min });
    if (f.max === Infinity) fail(EXPR_MSG.argCountMin, node, { signature, min: f.min });
    fail(EXPR_MSG.argCountRange, node, { signature, min: f.min, max: f.max });
  }

  private call(node: Extract<RawNode, { t: 'call' }>): Typed {
    const nameSpan = { from: node.from, to: node.nameTo };
    const f = findFunction(node.name);
    if (f === undefined) {
      const canon = canonicalName(node.name);
      if (ALL_VAR_NAMES.has(canon) || findConst(canon) !== undefined) {
        fail(EXPR_MSG.notAFunction, nameSpan, { name: canon });
      }
      const s = suggest(canon, FUNCTION_NAMES);
      if (s !== null) fail(EXPR_MSG.unknownFunctionSuggest, nameSpan, { name: node.name, suggestion: s });
      return fail(EXPR_MSG.unknownFunction, nameSpan, { name: node.name });
    }
    this.arity(f, node);
    if (f.usesRand) {
      this.usesRand = true;
      this.impure = true;
    }
    if (f.usesTempo) this.impure = true;
    if (f.name === 'alt') this.read('i');
    const span = sp(node);
    const call = (args: readonly Ir[]): Ir => ({ op: 'call', fn: f.name, args, from: span.from, to: span.to });
    const policy = f.policy;
    switch (policy.kind) {
      case 'numeric': {
        const trig = policy.trig === true;
        const checked = node.args.map((a) => this.check(a));
        const args = checked.map((t) => this.numeric(t, trig));
        if (f.name === 'bezier') this.validateBezier(args, node);
        return typed(
          call(args),
          'num',
          checked.some((t) => t.item),
        );
      }
      case 'any':
        // sign(x): -1, 0 or 1, a plain factor whatever x is.
        return typed(call(node.args.map((a) => this.check(a).ir)), 'num');
      case 'same': {
        // The values (the first `count` arguments) share one unit; the rest (lerp's u, round's digits) are
        // plain parameters that do not make the result a value of the item.
        const count = policy.count ?? node.args.length;
        let kind: Kind = 'num';
        let item = false;
        let lit: TimeLiteral | null = null;
        const args: Ir[] = [];
        for (let k = 0; k < node.args.length; k++) {
          const argNode = node.args[k] ?? node;
          const t = this.check(argNode);
          if (k < count) {
            kind = this.unify(kind, t.kind, argNode);
            item ||= t.item;
            lit ??= t.timeLit;
            args.push(t.ir);
          } else {
            args.push(this.numeric(t, false));
          }
        }
        return typed(call(args), kind, item, lit);
      }
      case 'ease':
        return typed(this.easeCall(node, call), 'num');
      case 'toBeat': {
        const arg = node.args[0] ?? node;
        const t = this.check(arg);
        if (t.kind !== 'time' && t.kind !== 'num') fail(EXPR_MSG.beatArg, arg);
        return typed(call([this.toSeconds(t.ir)]), 'beat');
      }
      case 'toSec': {
        const arg = node.args[0] ?? node;
        const t = this.check(arg);
        if (t.kind !== 'beat' && t.kind !== 'num') fail(EXPR_MSG.secArg, arg);
        const seconds = call([t.ir]);
        return typed(this.timeUnit === 'ms' ? scaleIr(seconds, 1000) : seconds, 'time');
      }
    }
  }

  private easeCall(node: Extract<RawNode, { t: 'call' }>, call: (args: readonly Ir[]) => Ir): Ir {
    const [first, ...rest] = node.args;
    if (first === undefined) return fail(EXPR_MSG.argCountRange, node, { signature: 'ease(curve, u)', min: 2, max: 4 });
    const id = this.easeId(first);
    const others = rest.map((a) => this.numeric(this.check(a), false));
    const [, l, r] = others;
    // Literal curve parts are validated now (live error while typing) instead of per item.
    if (id.op === 'num' && (l === undefined || l.op === 'num') && (r === undefined || r.op === 'num')) {
      const reason = Ease.validateFor(
        { kind: 'tween', id: id.value, left: l?.op === 'num' ? l.value : 0, right: r?.op === 'num' ? r.value : 1 },
        'general',
      );
      if (reason !== null) fail(reason.key, node, reason.params);
    }
    return call([id, ...others]);
  }

  /** Literal Bezier handles are validated now (live error while typing) instead of per item. */
  private validateBezier(args: readonly Ir[], span: Span): void {
    const p: number[] = [];
    for (const a of args.slice(0, 4)) {
      if (a.op !== 'num') return;
      p.push(a.value);
    }
    const [x1 = 0, y1 = 0, x2 = 1, y2 = 1] = p;
    const reason = Ease.validateFor({ kind: 'bezier', p: [x1, y1, x2, y2] }, 'general');
    if (reason !== null) fail(reason.key, span, reason.params);
  }

  private easeId(arg: RawNode): Ir {
    if (arg.t === 'str') return numIr(this.easingByName(arg.value, arg), arg);
    if (arg.t === 'id') {
      const canon = canonicalName(arg.name);
      if (findConst(canon) === undefined && findVar(this.varSet, canon) === undefined) {
        const id = easingIdByName(arg.name);
        if (id !== null) return numIr(id, arg);
        if (!ALL_VAR_NAMES.has(canon)) this.unknownEasing(arg.name, arg);
      }
    }
    const ir = this.numeric(this.check(arg), false);
    if (ir.op === 'num' && (!Number.isInteger(ir.value) || ir.value < 1 || ir.value > 29)) {
      fail(EXPR_MSG.easingId, arg);
    }
    return ir;
  }

  private easingByName(name: string, span: Span): number {
    const id = easingIdByName(name);
    if (id === null) this.unknownEasing(name, span);
    return id;
  }

  private unknownEasing(name: string, span: Span): never {
    // A typo of a curve name first ("outQaud"), then the easing picker's fuzzy search ("out qu", "bouncy").
    const typo = suggest(name.replace(/[^A-Za-z0-9]/g, ''), EASING_NAMES);
    const best = typo ?? (name.trim() === '' ? undefined : Ease.search(name)[0]?.rpeName);
    if (best !== undefined) fail(EXPR_MSG.unknownEasingSuggest, span, { name, suggestion: camel(best) });
    return fail(EXPR_MSG.unknownEasing, span, { name });
  }
}

/** Checks a raw program for a context and lowers it. Throws ExprFailure. */
export function checkProgram(src: string, raw: RawProgram, varSet: ExprVarSet, options?: ExprParseOptions): Checked {
  return new Checker(src, varSet, fieldUnitOf(options?.unit)).program(raw);
}
